import {
  createTheme,
  makeStyles,
  withStyles,
  createStyles,
  Theme,
} from '@material-ui/core/styles';
import { PaletteType, Color } from '@material-ui/core';
import red from '@material-ui/core/colors/red';
import grey from '@material-ui/core/colors/grey';
import orange from '@material-ui/core/colors/orange';
import green from '@material-ui/core/colors/green';
import {
  ColorPartial,
  CommonColors,
  PaletteColor,
  PaletteColorOptions,
  PaletteTonalOffset,
  TypeAction,
  TypeBackground,
  TypeDivider,
  TypeText,
} from '@material-ui/core/styles/createPalette';

type ColorString = string;

interface ColorGroup {
  main?: ColorString;
  [Key: number]: ColorString;
  A100?: ColorString;
  A200?: ColorString;
  A400?: ColorString;
  A700?: ColorString;
}

interface NordkapColors {
  lightBlue: ColorString;
  blue: ColorString;
  darkBlue: ColorString;
  lightPurple: ColorString;
  purple: ColorString;
  orange: ColorString;
  lightGreen: ColorString;
  green: ColorString;
  grey: ColorString;
  lightGrey: ColorString;
  red: ColorGroup;
}

interface ErrorPaletteColor extends PaletteColor {
  [Key: number]: ColorString;
}

export interface NordkapPalette {
  common: CommonColors;
  type: PaletteType;
  contrastThreshold: number;
  tonalOffset: PaletteTonalOffset;
  primary: PaletteColor;
  secondary: PaletteColor;
  error: ErrorPaletteColor;
  warning: PaletteColor;
  info: PaletteColor;
  success: PaletteColor;
  grey: Color;
  text: TypeText;
  divider: TypeDivider;
  action: TypeAction;
  background: TypeBackground;
  getContrastText: (background: string) => string;
  augmentColor: {
    (
      color: ColorPartial,
      mainShade?: number | string,
      lightShade?: number | string,
      darkShade?: number | string,
    ): PaletteColor;
    (color: PaletteColorOptions): PaletteColor;
  };
}

const primaryColors = {
  main: '#0070BE',
  light: '#589EF1',
  dark: '#00468D',
  contrastText: '#FFF',
  all: {
    50: '#e0eef7',
    100: '#b3d4ec',
    200: '#80b8df',
    300: '#4d9bd2',
    400: '#2685c8',
    500: '#0070be',
    600: '#0068b8',
    700: '#005daf',
    800: '#0053a7',
    900: '#004199',
    A100: '#c4d9ff',
    A200: '#91b7ff',
    A400: '#5e96ff',
    A700: '#4586ff',
  },
};

const secondaryColors = {
  main: '#A161E4',
  light: '#D591FF',
  dark: '#6E33B1',
  contrastText: '#FFF',
};

export const makeOpaque = (color: string, opacity: number = 0.5) => {
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'number' is not assignable to par... Remove this comment to see the full error message
  const hexString = parseInt((1 - opacity) * 255).toString(16);
  return `${color}${hexString}`;
};

interface NordkapTextOptions {
  primary: ColorString;
  secondary: ColorString;
}

interface NordkapOptions {
  fontSize: number;
  readFontSize: number;
  text: NordkapTextOptions;
  nordkapColors: NordkapColors;
}

export const options: NordkapOptions = {
  fontSize: 14,
  readFontSize: 14,
  text: {
    primary: '#000',
    secondary: grey[700],
  },
  nordkapColors: {
    lightBlue: '#98c2df',
    blue: '#0070BE',
    darkBlue: '#064470',
    lightPurple: '#dac1f3',
    purple: '#b384e6',
    orange: '#ff351f',
    lightGreen: '#a6e3d1',
    green: '#1BBB8B',
    grey: grey[300],
    lightGrey: '#f1f1f1',
    red,
  },
};

export const theme = createTheme({
  typography: {
    fontSize: options.fontSize,
  },
  palette: {
    type: 'light',
    primary: primaryColors,
    secondary: secondaryColors,
    error: red,
    text: options.text,
    // Used by `getContrastText()` to maximize the contrast between the background and
    // the text.
    contrastThreshold: 3,
    // Used to shift a color's luminance by approximately
    // two indexes within its tonal palette.
    // E.g., shift from Red 500 to Red 300 or Red 700.
    tonalOffset: 0.2,
    divider: '#d3d4d5',
    background: {
      secondary: '#d3d4d5',
    },
  },
  customProps: {
    inactiveOpacity: 0.7,
    header: {
      backgroundColor: '#f1f1f1',
      textColor: '#000',
      borderBottom: `1px solid ${grey[500]}`,
    },
    tabs: {
      bottomBorder: `4px solid ${makeOpaque(primaryColors.main)}`,
      activeBackgroundColor: makeOpaque(primaryColors.main),
      backgroundColor: '#f1f1f1',
      textColor: '#000',
    },
    table: {
      fontSize: 16,
    },
    backgroundColor: '#fff',
    actionColor: primaryColors.main,
  },
  overrides: {
    MuiTouchRipple: {
      ripple: {
        color: primaryColors.main,
      },
    },
    MuiFormControl: {
      marginDense: {
        marginTop: 0,
      },
    },
    MuiInputBase: {
      inputMarginDense: {
        paddingTop: 3,
        paddingBottom: 2,
      },
    },
    MuiInputLabel: {
      marginDense: {
        top: -3,
      },
    },
    MuiTypography: {
      h4: {
        color: grey[800],
      },
    },
    MuiCheckbox: {
      root: {
        padding: 9,
      },
    },
    MuiListItem: {
      root: {
        '&.Mui-selected': {
          backgroundColor: makeOpaque(options.nordkapColors.blue),
          '&:hover': {
            backgroundColor: makeOpaque(options.nordkapColors.blue),
          },
        },
      },
    },
    MuiTableCell: {
      root: {
        paddingTop: 8,
        paddingBottom: 8,
      },
      head: {
        paddingTop: 8,
        paddingBottom: 8,
        backgroundColor: options.nordkapColors.lightGrey,
      },
      footer: {
        color: options.text.primary,
      },
    },
  },
  props: {
    MuiTextField: {
      margin: 'dense',
    },
    MuiInputBase: {
      margin: 'dense',
    },
    MuiInputLabel: {
      margin: 'dense',
    },
  },
});

const nkStyles = (theme: Theme) => {
  return createStyles({
    paper: {
      flexGrow: 1,
      padding: 10,
      borderRadius: 0,
      boxShadow:
        '0px 0px 0px 0px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14)',
    },

    formControl: {
      '& .input-label': {
        // top: -6,
        // left: -3,
        backgroundColor: 'inherit',
        // backgroundColor: '#fff',
        zIndex: 1,
        // padding: '0 4px',
        color: '#9e9e9e',
      },
    },

    hidden: {
      display: 'none',
    },

    primary: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.light,
      },
      '&:disabled': {
        backgroundColor: grey[200],
      },
    },

    danger: {
      backgroundColor: theme.palette.error.main,
      color: '#fff',
      '&:hover': {
        backgroundColor: theme.palette.error.light,
      },
      '&:disabled': {
        backgroundColor: theme.palette.error.light,
      },
    },

    success: {
      backgroundColor: green[400],
      color: '#fff',
      '&:hover': {
        backgroundColor: green[200],
      },
      '&:disabled': {
        backgroundColor: green[100],
      },
    },

    warning: {
      backgroundColor: orange[400],
      color: '#fff',
      '&:hover': {
        backgroundColor: orange[200],
      },
      '&:disabled': {
        backgroundColor: orange[100],
      },
    },

    endAdornmentRoot: {
      marginLeft: 0,
    },

    endAdornmentIcon: {
      fontSize: 16,
      marginRight: 8,
    },

    selectIconWithEndAdornment: {
      right: 24,
    },

    defaultIcon: {
      color: grey[300],
    },
    successIcon: {
      color: green[400],
    },

    errorIcon: {
      color: red[400],
    },

    errorText: {
      color: red[400],
    },

    input: {
      padding: 7,
    },

    numberInput: {
      '&[type=number]::-webkit-inner-spin-button': {
        width: 30,
        height: 30,
      },
    },

    inputLabelShrink: {
      transform: 'translate(12px, -1px) scale(0.75) !important',
    },

    datepicker: {
      '& input': {
        padding: 7,
      },
      '& button': {
        padding: 2,
      },
    },

    inputInvalid: {
      border: `1px solid ${theme.palette.error.main}`,
    },

    scrollable: {
      overflowX: 'auto',
    },

    blur: {
      filter: 'blur(4px)',
      transition: '0.3s filter linear',
    },

    bold: {
      fontWeight: 600,
    },

    height100: {
      height: '100%',
    },

    height50: {
      height: '50%',
    },

    fullWidth: {
      width: '100%',
    },

    relativePosition: {
      position: 'relative',
    },

    absolutePosition: {
      position: 'absolute',
    },

    flex: {
      display: 'flex',
    },

    flexRow: {
      display: 'flex',
      flexDirection: 'row',
    },

    flexColumn: {
      display: 'flex',
      flexDirection: 'column',
    },

    flexStart: {
      alignSelf: 'flex-start',
    },

    flexEnd: {
      alignSelf: 'flex-end',
    },

    flexCenter: {
      alignSelf: 'center',
    },

    flexStretch: {
      alignSelf: 'stretch',
    },

    justifyContentSpaceBetween: {
      justifyContent: 'space-between',
    },

    alignContentSpaceBetween: {
      alignContent: 'space-between',
    },

    speedDialBtn: {
      color: options.nordkapColors.blue,
      backgroundColor: options.nordkapColors.lightGrey,
      '&.remove': {
        backgroundColor: options.nordkapColors.orange,
      },
      '&.save': {
        backgroundColor: options.nordkapColors.green,
      },
      '&.update, &.add': {
        backgroundColor: options.nordkapColors.blue,
      },
      '&.events': {
        backgroundColor: options.nordkapColors.purple,
      },
      '&.back, &.generateAll': {
        backgroundColor: options.nordkapColors.lightBlue,
      },
      '&.recalculate': {
        backgroundColor: options.nordkapColors.lightGreen,
      },
      '&.generateSpecial': {
        backgroundColor: options.nordkapColors.lightPurple,
      },
      '&.amortization': {
        backgroundColor: options.nordkapColors.darkBlue,
      },
      '&:hover': {
        opacity: 0.6,
      },
      '&:disabled': {
        backgroundColor: '#c8c8c8',
        color: '#e8e8e8',
      },
      '&.x-small': {
        width: 30,
        height: 30,
        minHeight: 30,
      },
      '&.active': {
        color: options.nordkapColors.green,
      },
      '&.inactive': {
        color: 'red !important',
      },
    },

    frame: {
      border: 'none',
      overflow: 'hidden',
      width: '100%',
      height: 'calc(100vh - 200px)',
    },

    extendedIcon: {
      marginRight: 3,
    },

    fieldHelperText: {
      marginLeft: '4px',
      marginRight: '4px',
    },

    validFieldText: {
      visibility: 'hidden',
    },
  });
};

export const useNkStyles = makeStyles(nkStyles);
export const withNkStyles = withStyles(nkStyles);

export function combineStyles(...styles: any[]) {
  return function CombineStyles(theme: any) {
    const outStyles = styles.map((arg) => {
      // Apply the "theme" object for style functions.
      if (typeof arg === 'function') {
        return arg(theme);
      }
      // Objects need no change.
      return arg;
    });

    return outStyles.reduce((acc, val) => Object.assign(acc, val));
  };
}

export const setBodyStyle = (document: any) => {
  const colors = ['red', 'green', 'blue', 'yellow', 'purple'];
  for (let i = 0; i < colors.length; i++) {
    const color = colors[i];
    setTimeout(() => {
      document.body.style.backgroundColor = color;
    }, 400 * i);
  }
  document.body.style.transform = 'rotate(720deg)';
  document.body.style.transition = '2s';

  setTimeout(() => {
    document.body.style = undefined;
  }, 2000);
};

export function createStyled(styles: any, options: any) {
  function Styled(props: any) {
    const { children, ...other } = props;
    return children(other);
  }

  return withStyles(styles, options)(Styled);
}
